import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, Pencil, Copy, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import NumInput from "@/components/NumInput";

const fmtVal = (c) => (c.type === "percent" ? `${Number(c.value).toLocaleString("id-ID", { maximumFractionDigits: 2 })}%` : `Rp ${Number(c.value).toLocaleString("id-ID")}`);

const BLANK = { code: "", title: "", type: "percent", value: 10, max_uses: 0, min_subtotal: 0, min_currency: "IDR", expires_at: "", active: true };
export const promoUrl = (code) => `${window.location.origin}/promo/${code}`;
const fmtMin = (c) => (c.min_subtotal ? (c.min_currency === "USD" ? `$${c.min_subtotal}` : `Rp ${Number(c.min_subtotal).toLocaleString("id-ID")}`) : "—");

function PromoForm({ initial, onSaved }) {
  const [f, setF] = useState({ ...BLANK, ...initial, expires_at: initial.expires_at ? initial.expires_at.slice(0, 10) : "" });
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const n = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    const body = { ...f, expires_at: f.expires_at ? `${f.expires_at}T23:59:59+00:00` : null };
    try {
      initial.coupon_id ? await api.put(`/admin/coupons/${initial.coupon_id}`, body) : await api.post("/admin/coupons", body);
      toast.success("Promotion saved");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4 text-sm" data-testid="promo-form">
      <Field label="Promotion title" className="sm:col-span-2"><input value={f.title} onChange={s("title")} placeholder="e.g. Ramadan Sale 15%" className={inputCls} data-testid="promo-title-input" /></Field>
      <Field label="Code"><input value={f.code} onChange={s("code")} className={`${inputCls} uppercase`} data-testid="coupon-code-input" /></Field>
      <Field label="Type"><select value={f.type} onChange={s("type")} className={inputCls} data-testid="coupon-type-select"><option value="percent">Percent</option><option value="fixed">Fixed (IDR)</option></select></Field>
      <Field label={f.type === "percent" ? "Value (%)" : "Value (IDR)"}><NumInput decimals={f.type === "percent" ? 2 : 0} value={f.value} onChange={n("value")} placeholder={f.type === "percent" ? "e.g. 12,5" : ""} className={inputCls} data-testid="coupon-value-input" /></Field>
      <Field label="Max uses (0 = unlimited)"><NumInput value={f.max_uses} onChange={n("max_uses")} className={inputCls} data-testid="coupon-max-uses-input" /></Field>
      <Field label="Minimum spend">
        <div className="flex gap-2">
          <select value={f.min_currency} onChange={s("min_currency")} className={`${inputCls} w-24`} data-testid="promo-min-currency"><option>IDR</option><option>USD</option></select>
          <NumInput decimals={f.min_currency === "USD" ? 2 : 0} value={f.min_subtotal} onChange={n("min_subtotal")} className={inputCls} data-testid="promo-min-spend" />
        </div>
      </Field>
      <Field label="Expires"><input type="date" value={f.expires_at} onChange={s("expires_at")} className={inputCls} data-testid="promo-expires-input" /></Field>
      <div className="sm:col-span-2 flex justify-end"><Btn onClick={save} data-testid="coupon-create-btn">Save promotion</Btn></div>
    </div>
  );
}

function ShareDialog({ c }) {
  const url = promoUrl(c.code);
  const text = `${c.title || "Promo Craftendemic"} — ${fmtVal(c)} off with code ${c.code}: ${url}`;
  const copy = async () => { await navigator.clipboard.writeText(url).catch(() => {}); toast.success("Link copied"); };
  return (
    <div className="space-y-4 text-sm" data-testid="promo-share">
      <div className="flex gap-2"><input readOnly value={url} className={inputCls} data-testid="promo-share-url" /><button onClick={copy} className="border border-[#3B220C] px-3" data-testid="promo-copy-btn" aria-label="Copy"><Copy size={14} /></button></div>
      <div className="flex flex-wrap gap-2">
        <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className="bg-[#2E6B40] text-white px-4 py-2" data-testid="promo-share-whatsapp">WhatsApp</a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer" className="bg-[#3B220C] text-white px-4 py-2" data-testid="promo-share-facebook">Facebook</a>
        <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" className="bg-[#1A1412] text-white px-4 py-2" data-testid="promo-share-x">X</a>
        {navigator.share && <button onClick={() => navigator.share({ title: c.title, text, url }).catch(() => {})} className="border border-[#3B220C] px-4 py-2">More…</button>}
      </div>
    </div>
  );
}

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [share, setShare] = useState(null);
  const load = () => api.get("/admin/coupons").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.coupon_id}`, null, { params: { active: !c.active } }); load(); };
  const del = async (c) => {
    if (!window.confirm(`Delete promotion ${c.code}?`)) return;
    try { await api.delete(`/admin/coupons/${c.coupon_id}`); toast.success("Promotion deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-coupons">
      <AdminTitle action={<Btn onClick={() => setEdit(BLANK)} data-testid="new-promo-btn">New promotion</Btn>}>With coupon</AdminTitle>
      <div className="bg-white border border-[#E6DFD5] divide-y divide-[#E6DFD5]">
        {rows.map((c) => (
          <div key={c.coupon_id} className="p-4 flex flex-wrap items-center gap-4 text-sm" data-testid={`coupon-row-${c.code}`}>
            <div className="flex-1 min-w-[180px]"><p className="font-medium">{c.title || "—"}</p><p className="font-mono tracking-widest text-[#8C4F27] text-xs">{c.code}</p></div>
            <span>{fmtVal(c)}</span>
            <span className="text-xs text-[#6E6259]">min {fmtMin(c)} · used {c.used}/{c.max_uses || "∞"}{c.expires_at && ` · until ${c.expires_at.slice(0, 10)}`}</span>
            <button onClick={() => setEdit(c)} aria-label="Edit" data-testid={`promo-edit-${c.code}`}><Pencil size={14} /></button>
            <button onClick={() => setShare(c)} aria-label="Share" data-testid={`promo-share-${c.code}`}><Share2 size={14} /></button>
            <Switch checked={c.active} onCheckedChange={() => toggle(c)} data-testid={`coupon-toggle-${c.code}`} />
            <button onClick={() => del(c)} aria-label="Delete" className="text-[#9E2A2B]" data-testid={`promo-delete-${c.code}`}><Trash2 size={14} /></button>
          </div>
        ))}
        {!rows.length && <p className="p-6 text-sm text-[#6E6259]">No promotions yet. Welcome codes are created automatically per new account.</p>}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-xl rounded-none">
          <DialogTitle className="font-serif text-3xl font-normal">{edit?.coupon_id ? "Edit promotion" : "New promotion"}</DialogTitle>
          {edit && <PromoForm initial={edit} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!share} onOpenChange={(o) => !o && setShare(null)}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogTitle className="font-serif text-3xl font-normal">Share promotion</DialogTitle>
          {share && <ShareDialog c={share} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
